import Auth from "src/services/auth";
import errorCodes, { StatusError } from "src/utils/request/errors";
import callWithRetry from "src/utils/request/retry";
import BackoffStrategy from "src/utils/request/backoff-strategy";
import type { RequestHeaders, RequestOptions } from "src/utils/request/types";
import { getBaseUrl } from "../url";

const mergeHeaders = (
    headers?: RequestHeaders
): Required<Pick<RequestHeaders, "authorization">> & RequestHeaders => {
    let tz;
    try {
        tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (_e) {
        //
    }
    return {
        ...(Auth.token &&
            Auth.token.access_token && {
                authorization: `Bearer ${Auth.token && Auth.token.access_token}`,
            }),
        ...(tz && { tz }),
        ...headers,
    };
};

const getMergedHeaders = (
    opts,
    headers?: RequestHeaders
): Required<Pick<RequestHeaders, "authorization">> & RequestHeaders => {
    if (!opts || !opts.body || !(opts.body instanceof FormData)) {
        return mergeHeaders({ "Content-Type": "application/json", ...headers });
    }
    return mergeHeaders(headers);
};

const executeRequest = async (
    url,
    opts: RequestHeaders & { headers?: RequestHeaders } & {
        signal?: AbortController["signal"];
    } = {}
) => {
    const { headers, ...rest } = opts;

    return fetch(`${getBaseUrl()}${url.startsWith("/") ? url : `/${url}`}`, {
        credentials: "same-origin",
        headers: {
            ...getMergedHeaders(opts, headers),
        },
        ...rest,
    });
};

const requestWithTimeout = async <T>({
    url,
    controller,
    timeout,
    opts,
}: {
    url: string;
    controller: AbortController;
    timeout?: number;
    opts: RequestOptions;
}): Promise<T> => {
    const { json, responseType, logoutWithoutRedirectOnUnauthorized, ...rest } = opts;

    const res = await Promise.race([
        executeRequest(url, { ...rest, signal: controller && controller.signal }),
        new Promise((_, reject) => {
            setTimeout(() => {
                if (controller) return controller.abort();
                return reject(new Error("timeout"));
            }, timeout || 30000);
        }),
    ]);

    const response = res as Response;

    if (response.status === 401) {
        if (logoutWithoutRedirectOnUnauthorized) {
            // @ts-expect-error this is gonna refresh the whole page
            return Auth.logout(logoutWithoutRedirectOnUnauthorized);
        }
        window.onbeforeunload = null;
        const refreshed = await Auth.refreshToken();
        if (refreshed) {
            return requestWithTimeout<T>({ url, controller, timeout, opts });
        }
    }
    if (!response.ok) {
        let message;
        try {
            message = (await response.clone().json()).message;
        } catch (_e) {
            message = `An error has occurred: ${response.status}`;
        }
        throw new StatusError(message, response.status);
    }
    // Fetch body can only be consumed once: clone the response so we have a backup that
    // can be read as plain text, in case of JSON parse failure.
    const textResponse = response.clone();
    try {
        if (responseType === "blob") {
            const blob = await response.blob();
            let filenameHeader;
            response.headers.forEach((value, key) => {
                if (key === "content-disposition") {
                    filenameHeader = value;
                }
            });
            let filename;
            if (filenameHeader) {
                [, filename] = filenameHeader.match(/filename=(.+$)/);
            }
            return { blob, filename } as T;
        }
        return (await response.json()) as T;
    } catch (_e) {
        return (await textResponse.text()) as T;
    }
};

const request = async <T>(url, opts: RequestOptions = {}): Promise<T> => {
    if (Auth.idToken && Auth.idToken.isDemo) {
        console.log("request skipped in demo mode", url, opts);
        console.log(`
                 -___________-
                (/     _     \\)
                /_____(O)_____\\
                // / / | \\ \\ \\\\
               =================
               // / | | | | \\ \\\\    "REGISTER! REGISTER!"
              ===================    /
             //// || || || || \\\\\\\\
             |||| || || || || ||||
            /---___-----------,---\\
            |  /   \\         -o-  |
            /  \\___/          '   \\
            +---------------------+
           /_   __    ___    __   _\\
          (__) (__)  (___)  (__) (__)
          |_    __    ___    __    _|
         (__)  (__)  (___)  (__)  (__)
         /_    ___    ___    ___    _\\
        (__)  (___)  (___)  (___)  (__)
        |_     ___    ___    ___     _|
       (__)   (___)  (___)  (___)   (__)
       /_______________________________\\
        `);
        // @ts-expect-error this is only for the demo
        return;
    }
    const { timeout, responseType, abortController, logoutWithoutRedirectOnUnauthorized, ...rest } =
        opts;
    let controller;
    if (typeof AbortController !== "undefined") {
        controller =
            abortController instanceof AbortController ? abortController : new AbortController();
    }
    try {
        // eslint-disable-next-line consistent-return
        return await callWithRetry(
            () =>
                requestWithTimeout<T>({
                    url,
                    controller,
                    timeout,
                    opts: { logoutWithoutRedirectOnUnauthorized, responseType, ...rest },
                }),
            new BackoffStrategy(BackoffStrategy.TYPES.exponential)
        );
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Network request failed:", e);
        if (logoutWithoutRedirectOnUnauthorized && e && e.status === 401) throw e;
        let message;
        if (e.status) {
            message = errorCodes[e.status] || errorCodes.default;
        } else {
            // eslint-disable-next-line no-console
            console.warn(`Network request timed out: ${url}`, e);
            message = errorCodes.timedout;
        }
        const event = new CustomEvent("requestError", { detail: { message } });
        window.dispatchEvent(event);
        throw e;
    }
};

export default request;
